/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { motion } from 'framer-motion';
import axiosClient from '../../../api/axiosClient';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const CreditManagementSettings = () => {
  const { isDark } = useTheme();
  const { t, i18n } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [salonCredits, setSalonCredits] = useState({
    credits_remaining: 0,
    credits_total_granted: 0,
    credits_used: 0,
  });
  
  const [billingPeriod, setBillingPeriod] = useState({
    start: null,
    end: null,
  });
  
  const [barbers, setBarbers] = useState([]);
  const [allocationInputs, setAllocationInputs] = useState({});
  const [allocatingBarber, setAllocatingBarber] = useState(null);

  // Load barbers with credit details
  const loadBarbersWithCredits = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosClient.get('/admin/credits/barbers');
      
      if (response.data) {
        setSalonCredits(response.data.salon || {});
        setBillingPeriod(response.data.billing_period || {});
        setBarbers(response.data.barbers || []);
        
        // Initialize allocation inputs
        const inputs = {};
        (response.data.barbers || []).forEach(barber => {
          inputs[barber.user_id] = '';
        });
        setAllocationInputs(inputs);
      }
    } catch (err) {
      console.error('[LOAD_BARBERS_ERROR]', err);
      setError(err.response?.data?.message || t('admin.settings_credits_load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBarbersWithCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle credit allocation
  const handleAllocateCredits = async (barberUserId) => {
    const creditsToAllocate = parseInt(allocationInputs[barberUserId], 10);
    
    if (!creditsToAllocate || creditsToAllocate <= 0) {
      setError(t('admin.settings_invalid_credits'));
      return;
    }
    
    if (creditsToAllocate > salonCredits.credits_remaining) {
      setError(t('admin.settings_not_enough_credits', { count: creditsToAllocate, remaining: salonCredits.credits_remaining }));
      return;
    }
    
    try {
      setAllocatingBarber(barberUserId);
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await axiosClient.post('/admin/credits/allocate', {
        barber_user_id: barberUserId,
        credits: creditsToAllocate,
      });
      
      if (response.data && response.data.success) {
        setSuccess(t('admin.settings_credits_allocated_success', { count: creditsToAllocate }));
        
        // Clear the input
        setAllocationInputs(prev => ({
          ...prev,
          [barberUserId]: '',
        }));
        
        // Reload data
        await loadBarbersWithCredits();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('[ALLOCATE_CREDITS_ERROR]', err);
      setError(err.response?.data?.message || t('admin.settings_credits_allocate_error'));
    } finally {
      setSaving(false);
      setAllocatingBarber(null);
    }
  };

  // Handle input change
  const handleInputChange = (barberUserId, value) => {
    setAllocationInputs(prev => ({
      ...prev,
      [barberUserId]: value,
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: ACCENT }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
        >
          <div className="flex items-start">
            <svg className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </motion.div>
      )}

      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
        >
          <div className="flex items-start">
            <svg className="h-5 w-5 text-green-600 dark:text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
          </div>
        </motion.div>
      )}

      {/* Salon Credits Overview */}
      <Card>
        <CardHeader
          title={t('admin.settings_credits_title')}
          subtitle={t('admin.settings_credits_subtitle')}
          icon={
            <svg className="h-5 w-5" style={{ color: ACCENT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-zinc-50 dark:bg-white/5 rounded-xl p-4 border border-zinc-100 dark:border-white/5">
              <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">
                {t('admin.settings_credits_total_granted')}
              </div>
              <div className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                {salonCredits.credits_total_granted || 0}
              </div>
            </div>
            
            <div className="bg-zinc-50 dark:bg-white/5 rounded-xl p-4 border border-zinc-100 dark:border-white/5">
              <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">
                {t('admin.settings_credits_used')}
              </div>
              <div className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                {salonCredits.credits_used || 0}
              </div>
            </div>
            
            <div className="bg-zinc-50 dark:bg-white/5 rounded-xl p-4 border border-zinc-100 dark:border-white/5">
              <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-1">
                {t('admin.settings_credits_available')}
              </div>
              <div className="text-2xl font-bold" style={{ color: ACCENT }}>
                {salonCredits.credits_remaining || 0}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-white/10">
            <div className="text-sm text-zinc-600 dark:text-zinc-400">
              <span className="font-medium">{t('admin.settings_billing_period')}</span>{' '}
              {formatDate(billingPeriod.start)} - {formatDate(billingPeriod.end)}
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">
              {t('admin.settings_credits_reset_note')}
            </div>
          </div>
        </div>
      </Card>

      {/* Barbers List with Allocation */}
      <Card>
        <CardHeader
          title={t('admin.settings_barber_credits_title')}
          subtitle={t('admin.settings_barber_credits_subtitle')}
          icon={
            <svg className="h-5 w-5" style={{ color: ACCENT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />

        <div className="p-4 md:p-6">
          {barbers.length === 0 ? (
            <div className="text-center text-zinc-600 dark:text-zinc-400 py-8">
              <svg className="h-12 w-12 mx-auto mb-3 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-sm">{t('admin.settings_no_barbers_credits')}</p>
              <p className="text-xs mt-1">{t('admin.settings_add_barbers_hint')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {barbers.map((barber) => (
                <div key={barber.user_id} className="p-5 bg-zinc-50 dark:bg-white/5 rounded-xl border border-zinc-100 dark:border-white/5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {barber.avatar_url ? (
                        <img
                          src={barber.avatar_url}
                          alt={barber.name}
                          className="h-10 w-10 rounded-full object-cover ring-2 ring-white dark:ring-zinc-700"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 dark:from-zinc-700 dark:to-zinc-800 flex items-center justify-center">
                          <svg className="h-6 w-6 text-zinc-500 dark:text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-zinc-900 dark:text-zinc-100">
                          {barber.name}
                        </div>
                        <div className="text-sm text-zinc-600 dark:text-zinc-400">
                          {barber.email}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Credit Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white dark:bg-white/10 rounded-lg p-3 border border-zinc-200 dark:border-white/10">
                      <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-1">
                        {t('admin.settings_allocated')}
                      </div>
                      <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                        {barber.credits.allocated || 0}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-white/10 rounded-lg p-3 border border-zinc-200 dark:border-white/10">
                      <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-1">
                        {t('admin.settings_credits_used')}
                      </div>
                      <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                        {barber.credits.used || 0}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-white/10 rounded-lg p-3 border border-zinc-200 dark:border-white/10">
                      <div className="text-xs text-zinc-600 dark:text-zinc-400 mb-1">
                        {t('admin.settings_remaining')}
                      </div>
                      <div className="text-lg font-semibold" style={{ 
                        color: barber.credits.remaining > 0 ? '#10b981' : '#ef4444' 
                      }}>
                        {barber.credits.remaining || 0}
                      </div>
                    </div>
                  </div>

                  {/* Allocation Input */}
                  <div className="flex items-end space-x-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                        {t('admin.settings_allocate_credits_label')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={salonCredits.credits_remaining}
                        value={allocationInputs[barber.user_id] || ''}
                        onChange={(e) => handleInputChange(barber.user_id, e.target.value)}
                        placeholder={t('admin.settings_enter_amount')}
                        disabled={saving || salonCredits.credits_remaining === 0}
                        className="w-full px-4 py-2.5 border border-zinc-300 dark:border-zinc-600 rounded-xl bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        style={{ focusRingColor: ACCENT }}
                      />
                    </div>
                    <button
                      onClick={() => handleAllocateCredits(barber.user_id)}
                      disabled={
                        saving ||
                        !allocationInputs[barber.user_id] ||
                        parseInt(allocationInputs[barber.user_id], 10) <= 0 ||
                        parseInt(allocationInputs[barber.user_id], 10) > salonCredits.credits_remaining ||
                        allocatingBarber === barber.user_id
                      }
                      className="px-5 py-2.5 rounded-xl font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 hover:opacity-90"
                      style={{ backgroundColor: ACCENT }}
                    >
                      {allocatingBarber === barber.user_id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          <span>{t('admin.settings_allocating')}</span>
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span>{t('admin.settings_allocate')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Info Box */}
      <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800/30">
        <div className="p-4 md:p-5">
          <div className="flex items-start">
            <div className="h-10 w-10 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0 mr-3">
              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-sm text-blue-900 dark:text-blue-100">
              <p className="font-semibold mb-2">{t('admin.settings_how_credits_work')}</p>
              <ul className="space-y-1.5 text-xs text-blue-800 dark:text-blue-200">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('admin.settings_credits_bullet_1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('admin.settings_credits_bullet_2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('admin.settings_credits_bullet_3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('admin.settings_credits_bullet_4')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{t('admin.settings_credits_bullet_5')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CreditManagementSettings;
